import { Spinner } from '@/components/glitch-ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import {
  userNotificationSettingsQueryOptions,
  useUpdateUserNotificationSettingsMutation,
} from '@/lib/query-options';
import { AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/useToast';

export const EmailPreferences = () => {
  const { toast } = useToast();

  const {
    data: settings,
    isLoading,
    isError,
  } = useQuery(userNotificationSettingsQueryOptions());

  const updateMutation = useUpdateUserNotificationSettingsMutation();

  const handleToggle = async (enabled: boolean) => {
    try {
      await updateMutation.mutateAsync({ email_notifications_enabled: enabled });
      toast({
        title: enabled ? 'Email notifications enabled' : 'Email notifications disabled',
        description: enabled
          ? 'You will receive email notifications for platform alerts.'
          : 'You will no longer receive email notifications.',
      });
    } catch {
      toast({
        title: 'Failed to update settings',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <Card className="rounded-lg border bg-white">
        <CardContent className="p-6 flex items-center justify-center">
          <Spinner size="md" className="text-gray-500" />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="rounded-lg border bg-white border-red-200">
        <CardContent className="p-6 flex items-center gap-3 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">Failed to load notification settings</span>
        </CardContent>
      </Card>
    );
  }

  const isSubscribed = settings?.email_notifications_enabled ?? false;

  return (
    <Card className="rounded-lg border bg-white">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-gray-900 font-medium">Email Notifications</h3>
            <p className="text-gray-700 text-sm">
              Receive emails for optimization recommendations, campaign updates, and system alerts.
            </p>
          </div>
          <Switch
            checked={isSubscribed}
            onCheckedChange={handleToggle}
            disabled={updateMutation.isPending}
            className="ml-4"
          />
        </div>
      </CardContent>
    </Card>
  );
};

